import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';

interface DashboardStats {
  totalLocations: number;
  totalChargePoints: number;
  activeChargingSessions: number;
  chargePointsByStatus: {
    available: number;
    'in-use': number;
    maintenance: number;
    offline: number;
  };
  locationsByStatus: {
    active: number;
    inactive: number;
    maintenance: number;
  };
}

const Dashboard: React.FC = () => {
  const { t } = useTranslation();
  const [stats, setStats] = useState<DashboardStats>({
    totalLocations: 0,
    totalChargePoints: 0,
    activeChargingSessions: 0,
    chargePointsByStatus: {
      available: 0,
      'in-use': 0,
      maintenance: 0,
      offline: 0
    },
    locationsByStatus: {
      active: 0,
      inactive: 0,
      maintenance: 0
    }
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchDashboardStats = async () => {
    try {
      const response = await api.get('/operators/dashboard');
      setStats(response.data);
      setError(null);
    } catch (err) {
      setError('Failed to fetch dashboard statistics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardStats();
    // Set up polling for real-time updates
    const interval = setInterval(fetchDashboardStats, 30000); // Update every 30 seconds

    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <div className="container mx-auto p-4">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto p-4">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4">
      <h1 className="text-2xl font-bold mb-6">{t('dashboard.title')}</h1>
      
      {/* Main Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
        <div className="bg-white p-6 rounded-lg shadow-md">
          <h2 className="text-lg font-semibold text-gray-700">{t('dashboard.stats.totalLocations')}</h2>
          <p className="text-3xl font-bold text-blue-600">{stats.totalLocations}</p>
          <div className="mt-2 text-sm text-gray-500">
            {t('dashboard.stats.status.active')}: {stats.locationsByStatus.active} | 
            {t('dashboard.stats.status.maintenance')}: {stats.locationsByStatus.maintenance} |
            {t('dashboard.stats.status.inactive')}: {stats.locationsByStatus.inactive}
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-md">
          <h2 className="text-lg font-semibold text-gray-700">{t('dashboard.stats.totalChargePoints')}</h2>
          <p className="text-3xl font-bold text-green-600">{stats.totalChargePoints}</p>
          <div className="mt-2 text-sm text-gray-500">
            {t('dashboard.stats.status.available')}: {stats.chargePointsByStatus.available} |
            {t('dashboard.stats.status.inUse')}: {stats.chargePointsByStatus['in-use']} |
            {t('dashboard.stats.status.maintenance')}: {stats.chargePointsByStatus.maintenance} |
            {t('dashboard.stats.status.offline')}: {stats.chargePointsByStatus.offline}
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-md">
          <h2 className="text-lg font-semibold text-gray-700">{t('dashboard.stats.activeChargingSessions')}</h2>
          <p className="text-3xl font-bold text-purple-600">{stats.activeChargingSessions}</p>
          <div className="mt-2 text-sm text-gray-500">
            {t('dashboard.stats.updatedRealtime')}
          </div>
        </div>
      </div>

      {/* Status Indicators */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
        <div className="bg-white p-6 rounded-lg shadow-md">
          <h2 className="text-lg font-semibold text-gray-700 mb-4">{t('dashboard.stats.chargePointStatus')}</h2>
          <div className="space-y-2">
            {Object.entries(stats.chargePointsByStatus).map(([status, count]) => (
              <div key={status} className="flex items-center justify-between">
                <span className="capitalize">{t(`dashboard.stats.status.${status === 'in-use' ? 'inUse' : status}`)}</span>
                <div className="flex items-center">
                  <div className={`w-3 h-3 rounded-full mr-2 ${
                    status === 'available' ? 'bg-green-500' :
                    status === 'in-use' ? 'bg-blue-500' :
                    status === 'maintenance' ? 'bg-yellow-500' :
                    'bg-red-500'
                  }`}></div>
                  <span>{count}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-md">
          <h2 className="text-lg font-semibold text-gray-700 mb-4">{t('dashboard.stats.locationStatus')}</h2>
          <div className="space-y-2">
            {Object.entries(stats.locationsByStatus).map(([status, count]) => (
              <div key={status} className="flex items-center justify-between">
                <span className="capitalize">{t(`dashboard.stats.status.${status}`)}</span>
                <div className="flex items-center">
                  <div className={`w-3 h-3 rounded-full mr-2 ${
                    status === 'active' ? 'bg-green-500' :
                    status === 'maintenance' ? 'bg-yellow-500' :
                    'bg-red-500'
                  }`}></div>
                  <span>{count}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Last Updated */}
      <div className="text-right text-sm text-gray-500">
        {t('dashboard.stats.lastUpdated')}: {new Date().toLocaleTimeString()}
      </div>
    </div>
  );
};

export default Dashboard; 